import { Container, Graphics } from "pixi.js";
import { TILE_H, TILE_W } from "@vinyl/shared";
import { tileToScreen } from "./iso.js";

// Persistent ground-targeted spell visuals (Rain of Fire so far). Each effect
// is a ring at the target tile that pulses gently for its duration. Children
// are added/removed as `areaEffectSpawned` / -Expired events arrive.

export interface AreaEffectSpawnPayload {
  areaId: string;
  spellId: string;
  casterId: string;
  x: number;
  y: number;
  radius: number;
  expiresAt: number;
}

interface AreaView {
  areaId: string;
  spellId: string;
  graphic: Graphics;
  expiresAt: number;
  spawnedAt: number;
}

// Visual params per spell — colour and animation feel. Falls back to the
// generic arcane palette when a spell isn't listed.
const VISUAL_BY_SPELL: Record<
  string,
  { color: number; alpha: number }
> = {
  rain_of_fire: { color: 0xff7a2d, alpha: 0.4 }, // burnt orange
};

export class AreaEffectLayer {
  readonly container = new Container();
  private readonly views = new Map<string, AreaView>();

  constructor() {
    this.container.label = "area-effects";
  }

  spawn(payload: AreaEffectSpawnPayload, now = performance.now()): void {
    if (this.views.has(payload.areaId)) return;
    const g = new Graphics();
    const { sx, sy } = tileToScreen(payload.x, payload.y);
    g.position.set(sx, sy);
    this.drawRing(g, payload.spellId, payload.radius);
    this.container.addChild(g);
    this.views.set(payload.areaId, {
      areaId: payload.areaId,
      spellId: payload.spellId,
      graphic: g,
      expiresAt: payload.expiresAt,
      spawnedAt: now,
    });
  }

  expire(areaId: string): void {
    const view = this.views.get(areaId);
    if (!view) return;
    this.views.delete(areaId);
    this.container.removeChild(view.graphic);
    view.graphic.destroy();
  }

  clear(): void {
    for (const v of this.views.values()) {
      this.container.removeChild(v.graphic);
      v.graphic.destroy();
    }
    this.views.clear();
  }

  // Called every frame (or tick) so we can pulse alpha across the lifetime.
  tick(_now: number): void {
    // Pulse logic placeholder — kept stable for now so the static ring is
    // easy to read. Animation polish can land in M4.5 if needed.
  }

  destroy(): void {
    for (const v of this.views.values()) {
      this.container.removeChild(v.graphic);
      v.graphic.destroy();
    }
    this.views.clear();
    this.container.destroy({ children: true });
  }

  private drawRing(g: Graphics, spellId: string, radiusTiles: number): void {
    const visual = VISUAL_BY_SPELL[spellId] ?? { color: 0xc8a2ff, alpha: 0.35 };
    // The isometric tile is 64×32 with the centre at (0, 0) — render the
    // AOE as an ellipse matching the iso projection of a circle (so it
    // reads as a flat disc on the ground rather than a screen-space oval).
    const rx = (radiusTiles + 0.5) * TILE_W * 0.5;
    const ry = (radiusTiles + 0.5) * TILE_H * 0.5;
    g.ellipse(0, 0, rx, ry)
      .fill({ color: visual.color, alpha: visual.alpha * 0.4 })
      .stroke({ color: visual.color, alpha: visual.alpha + 0.2, width: 2 });
    // Light inner ring at the centre tile so the player can see exactly
    // where they clicked.
    g.ellipse(0, 0, TILE_W * 0.35, TILE_H * 0.35)
      .stroke({ color: visual.color, alpha: 0.7, width: 1 });
  }
}
